import dayjs, { Dayjs } from 'dayjs';
import momentTimezone from 'moment-timezone';
import i18n from '@assets/i18n/index';
import localesConfig from '@config/locales.config';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import('dayjs/locale/ja.js');
import('dayjs/locale/en.js');
import('dayjs/locale/zh-cn.js');

let initDayjsLocal = false;

function setDayjsLocal() {
    if (initDayjsLocal) return;

    const { locale } = i18n?.global || {};
    const localeInfo = localesConfig.find(item => item.key === locale);
    if (localeInfo) {
        const key = localeInfo.dayjsKey;
        dayjs.locale(key);
        dayjs.Ls[key].weekStart = 1; // 设置周起始日为周一
    }
}

/**
 * 不污染Date及prototype对象为原则
 * @type {{add: module.exports.add}}
 */
/**
 * 是否是日期类型
 * @param obj
 * @returns {boolean}
 */
function isDate(obj) {
    return Object.prototype.toString(obj) === '[object Date]';
}

/**
 * 将字符串解析为日期对象
 * @param str
 * @returns {Date}
 */
function parse(str) {
    return new Date(str);
}

/**
 * 获取该日期所在月份的第一天
 * @param date
 * @returns {*}
 */
function getFirstDayOfDate(date) {
    var cloned = this.clone(date);
    cloned.setDate(1);
    return cloned;
}

/**
 * 清除时分秒和毫秒
 * @param date
 */
function clearTime(date) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

/**
 * 克隆日期对象
 * @param date
 * @returns {Date}
 */
function clone(date) {
    return new Date(date.getTime());
}

/**
 * 增加(减少)天数
 * @param date
 * @param num
 * @returns {*}
 */
function addDays(date, num) {
    date.setDate(date.getDate() + num);
    return date;
}

/**
 * 月份操作
 * @param date
 * @param num
 * @returns {*}
 */
function addMonths(date, num) {
    date.setMonth(date.getMonth() + num);
    return date;
}

/**
 * 年份操作
 * @param date
 * @param num
 */
function addYears(date, num) {
    date.setFullYear(date.getFullYear() + num);
    return date;
}

/**
 * 比较两个日期,如果第一个大于第二个则返回1, 小于则返回-1, 相等则返回0
 * @param date1
 * @param date2
 * @returns {number}
 */
function compare(date1, date2) {
    if (date1.valueOf() < date2.valueOf()) {
        return -1;
    } else if (date1.valueOf() > date2.valueOf()) {
        return 1;
    }
    return 0;
}

/**
 * 通过增加时间来改变原始
 * api-link: http://momentjs.cn/docs/#/manipulating/add/
 */
function momentAdd(date, num, type = 'days', format = 'YYYY/MM/DD') {
    setDayjsLocal();
    return dayjs(new Date(date)).add(num, type).format(format);
}

/**
 * @param {Number} num
 * @param {String} type: [year, month, week, day]
 * @param {Number} isStart
 */
function getMomentTime({ num, type, isStart, timezone, format = 'YYYY/MM/DD' }) {
    setDayjsLocal();
    const nowTime = new Date().getTime();
    const momentItem = timezone ? formatTimezone(nowTime, timezone) : dayjs();
    const date = dayjs(momentItem).add(num, type);
    return (isStart ? date.startOf(type) : date.endOf(type)).format(format);
}

function formatDate(date, format = 'YYYY/MM/DD') {
    return dayjs(date).format(format);
}

function getDateForTimezone(timezone?: string, date?: number) {
    const targetDate = date || new Date();
    const localTimezone = momentTimezone.tz.guess();
    const targetTime = momentTimezone.tz(targetDate, timezone || localTimezone);
    return new Date(momentTimezone(targetTime).tz(localTimezone));
}

/**
 * 获取两个时间之间的天数
 * api-link: http://momentjs.cn/docs/#/displaying/difference/
 * @param {String} startDate: '2020/03/20'
 * @param {*} endDate: '2020/03/31'
 * @returns {Number}
 */
function getDateDiff(
    startDate: string | number | Date,
    endDate: string | number | Date,
    type = 'days',
    includeToday = true
) {
    setDayjsLocal();
    const a = dayjs(new Date(endDate));
    const b = dayjs(new Date(startDate));
    const days = a.diff(b, type);
    return includeToday ? days + 1 : days;
}

/**
 * 获取连续时间
 * @param {*} startDate: '2020/03/24'
 * @param {*} endDate: '2020/03/31'
 */
function getDays(
    startDate: string | number | Date,
    endDate: string | number | Date,
    format = 'YYYY/MM/DD'
) {
    setDayjsLocal();
    const dates = [];
    let currDate = dayjs(new Date(startDate)).startOf('day');
    const lastDate = dayjs(new Date(endDate)).startOf('day');
    while (currDate.add(1, 'days').diff(lastDate) < 0) {
        currDate = currDate.add(1, 'days');
        dates.push(currDate.clone().format(format));
    }
    return [startDate, ...dates, endDate];
}
/**
 * 获取连续周(不按自然周的)
 * @param startDate
 * @param endDate
 * @param format
 */
function getWeeks(
    startDate: string | number | Date,
    endDate: string | number | Date,
    format = 'YYYY/MM/DD'
) {
    setDayjsLocal();
    const dates = [];
    let currDate = dayjs(new Date(startDate)).startOf('day');
    const lastDate = dayjs(new Date(endDate)).startOf('day');
    while (currDate.add(7, 'days').diff(lastDate, 'days') > 7) {
        currDate = currDate.add(7, 'days');
        dates.push(currDate.clone().format(format));
    }

    return [startDate, ...dates];
}
/**
 * 获取连续自然周
 * @param startDate
 * @param endDate
 * @param format
 */
function getNatureWeeks(
    startDate: string | number | Date,
    endDate: string | number | Date,
    format = 'YYYY/MM/DD'
) {
    setDayjsLocal();
    const dates = [];
    let currDate = dayjs(new Date(startDate)).startOf('day');
    const lastDate = dayjs(new Date(endDate)).startOf('day');
    let firstDay = new Date(startDate).getDay();
    const lastDay = new Date(endDate).getDay();
    if (firstDay === 1) {
        dates.push(currDate.clone().format(format));
    } else {
        firstDay === 0 && (firstDay = 7);
        dates.push(
            currDate
                .subtract(firstDay - 1, 'day')
                .clone()
                .format(format)
        );
        currDate = currDate.subtract(firstDay - 1, 'day');
    }
    while (Math.abs(currDate.diff(lastDate, 'day')) > 7) {
        currDate = currDate.add(7, 'days');
        dates.push(currDate.clone().format(format));
    }
    if (Math.abs(currDate.diff(lastDate, 'day')) <= 7) {
        return dates;
    }
    if (lastDay === 1) {
        dates.push(lastDate.clone().format(format));
    } else {
        dates.push(
            lastDate
                .subtract(lastDay - 1, 'day')
                .clone()
                .format(format)
        );
    }
    return dates;
}
function getMonths(
    startDate: string | number | Date,
    endDate: string | number | Date,
    format = 'YYYY/MM'
) {
    setDayjsLocal();
    const dates = [];
    let currDate = dayjs(new Date(startDate)).startOf('month');
    const lastDate = dayjs(new Date(endDate)).startOf('month');
    while (Math.abs(currDate.diff(lastDate, 'month')) > 0) {
        currDate = currDate.add(1, 'month');
        dates.push(currDate.clone().format(format));
    }
    if (lastDate.month() !== currDate.month()) {
        dates.push(lastDate.clone().format(format));
    }
    return [dayjs(startDate).format(format), ...dates];
}

/**
 * 获取24小时数组
 */
function getHours() {
    return Array(24)
        .fill('')
        .map((item, index) => {
            const num = index < 10 ? `0${index}` : index;
            return `${num}:00`;
        });
}
// isCsvExport: 设置为true时，输出的时间间格式为 h:mm:ss，主要在csv导出时使用。
function getDuration(timestamp: string, isCsvExport: boolean = false) {
    timestamp = Number.parseInt(timestamp);
    const days = timestamp / 1000 / 60 / 60 / 24;
    const daysRound = Math.floor(days);
    const hours = timestamp / 1000 / 60 / 60 - 24 * daysRound;
    const hoursRound = Math.floor(hours);
    const minutes = timestamp / 1000 / 60 - 24 * 60 * daysRound - 60 * hoursRound;
    const minutesRound = Math.floor(minutes);
    const seconds = Number.parseInt(
        timestamp / 1000 - 24 * 60 * 60 * daysRound - 60 * 60 * hoursRound - 60 * minutesRound
    );
    let time = `${minutesRound > 9 ? minutesRound : '0' + minutesRound}:${
        seconds > 9 ? seconds : '0' + seconds
    }`;
    (hoursRound || isCsvExport) &&
        (time = `${hoursRound > 9 ? hoursRound : '0' + hoursRound}:${time}`);
    return time;
}

function getPrevMonth(date) {
    setDayjsLocal();
    const dateObj = dayjs(date).add(-1, 'month');
    return {
        startTime: dateObj.startOf('month'),
        endTime: dateObj.endOf('month')
    };
}

/**
 * 获取指定日期
 * state: {
 *  value,
 *  isIncludeToday
 * }
 */
function getDate(dateKey, timezone, state) {
    let startTime = null;
    let endTime = null;
    switch (dateKey) {
        case 'today':
            startTime = endTime = getMomentTime({
                num: 0,
                type: 'day',
                isStart: true,
                timezone
            });
            break;
        case 'yesterday':
            startTime = endTime = getMomentTime({
                num: -1,
                type: 'day',
                isStart: true,
                timezone
            });
            break;
        case 'this_week':
            startTime = getMomentTime({
                num: 0,
                type: 'week',
                isStart: true,
                timezone
            });
            endTime = getMomentTime({
                num: 0,
                type: 'day',
                isStart: false,
                timezone
            });
            break;
        case 'last_week':
            startTime = getMomentTime({
                num: -1,
                type: 'week',
                isStart: true,
                timezone
            });

            endTime = getMomentTime({
                num: -1,
                type: 'week',
                isStart: false,
                timezone
            });
            break;
        case 'this_month':
            startTime = getMomentTime({
                num: 0,
                type: 'month',
                isStart: true,
                timezone
            });
            endTime = getMomentTime({
                num: 0,
                type: 'day',
                isStart: false,
                timezone
            });
            break;
        case 'last_month':
            startTime = getMomentTime({
                num: -1,
                type: 'month',
                isStart: true,
                timezone
            });
            endTime = getMomentTime({
                num: -1,
                type: 'month',
                isStart: false,
                timezone
            });
            break;
        case 'past':
            const { value, isIncludeToday } = state;
            const startDays = -(value - (isIncludeToday ? 1 : 0));
            const endDays = isIncludeToday ? 0 : -1;
            startTime = getMomentTime({
                num: startDays,
                type: 'day',
                isStart: true,
                timezone
            });
            endTime = getMomentTime({
                num: endDays,
                type: 'day',
                isStart: false,
                timezone
            });
            break;
    }
    return {
        startTime,
        endTime
    };
}

/**
 * 转换到对应时区（获取指定时间在指定时区下对应的时间）
 * API: https://day.js.org/docs/zh-CN/timezone/converting-to-zone
 */
function convertToTimeZone(
    time: string | number,
    localTimezone: string,
): Dayjs {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    return dayjs(time).tz(localTimezone);
}

/**
 * 解析时区 （获取对应时区下指定时间）
 * API: https://day.js.org/docs/zh-CN/timezone/parsing-in-zone
 */
function parsingInZone(time: string | number, timeZone: string): Dayjs {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    return dayjs.tz(time, timeZone);
}

function formatTimezone(
    time: string | number,
    localTimezone: string,
    format = 'YYYY/MM/DD'
) {
    return convertToTimeZone(time, localTimezone).format(format);
}

function getTimeStampWithTimezone(
    timeStr: string,
    timezone: string,
    isEndTime?: boolean | undefined,
    isSecond: boolean = true
) {
    const timestamp = dayjs
        .tz(isEndTime ? dayjs(timeStr).format('YYYY/MM/DD') + ' 23:59:59' : timeStr, timezone)
        .toDate()
        .getTime()
        .toString();
    return isSecond ? Number(timestamp.slice(0, -3)) : Number(timestamp);
}

export default {
    isDate,
    parse,
    getFirstDayOfDate,
    clearTime,
    clone,
    addDays,
    addMonths,
    addYears,
    compare,
    formatDate,
    momentAdd,
    getMomentTime,
    getDate,
    getDateForTimezone,
    getDateDiff,
    getDays,
    getNatureWeeks,
    getWeeks,
    getMonths,
    getHours,
    getDuration,
    getPrevMonth,
    convertToTimeZone,
    parsingInZone,
    formatTimezone,
    getTimeStampWithTimezone
};
